/**
 * FloatingNav
 *
 * Generic horizontal floating navigation bar with click-to-toggle dropdowns.
 *
 * Interaction model:
 * - Click/tap on a category always toggles its dropdown — works on any device.
 * - On hover-capable pointer devices (`@media (hover: hover) and (pointer: fine)`),
 *   hover ALSO opens the dropdown for snappier desktop feel. Touch devices
 *   never receive hover handlers — avoids the iOS sticky-hover trap where the
 *   first tap fires :hover but not :click, requiring a second tap.
 *
 * Layout:
 * - One render path for the dropdown content. CSS owns viewport-specific
 *   positioning (centered popover on >=768px; full-width sheet on <768px).
 *   The previous version split rendering into two JSX branches (desktop hover
 *   vs. touch sheet) and gated the touch sheet with `md:hidden`, leaving
 *   touch-capable tablets (iPads, Surface) in a dead zone where neither
 *   branch rendered.
 *
 * Used by HR, Finance, Ministry, Operations, Messaging, etc.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export interface FloatingNavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  description: string;
  /** Optional indicator: count of new/unread items in this section */
  changeCount?: number;
  /** Optional indicator label (e.g., "New", "Updated") shown as a badge */
  changeLabel?: string;
}

export interface FloatingNavCategory {
  group: string;
  /** Optional icon displayed next to the category label in the nav bar */
  groupIcon?: React.ElementType;
  /** Optional visible affordance for category buttons that open menus. */
  menuHint?: string;
  items: FloatingNavItem[];
}

interface FloatingNavProps {
  categories: FloatingNavCategory[];
  activeSection: string;
  onNavigate: (id: string) => void;
  /** Optional CSS class for module-specific accent coloring */
  className?: string;
  /** Module accent hex color (e.g. "#1a7a4c" for Finance green) */
  moduleAccent?: string;
}

/** Convert hex color to RGBA-as-hex tint with given alpha. */
function hexToTint(hex: string, alpha = 0.12): string {
  return `${hex}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
}

function activeGroupForSection(
  categories: FloatingNavCategory[],
  sectionId: string,
): string {
  for (const cat of categories) {
    if (cat.items.some((item) => item.id === sectionId)) return cat.group;
  }
  return categories[0]?.group ?? "";
}

/**
 * Track whether the primary pointer supports true hover (mouse / trackpad).
 * Mirrors the CSS gate `@media (hover: hover) and (pointer: fine)` so the JS
 * hover handlers stay in sync with the CSS :hover rules. matchMedia listener
 * keeps it accurate if the user attaches/detaches a mouse mid-session.
 */
function useHoverCapable(): boolean {
  const [hoverCapable, setHoverCapable] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(hover: hover) and (pointer: fine)");
    const onChange = (e: MediaQueryListEvent) => setHoverCapable(e.matches);
    // Safari < 14 only supports the deprecated addListener API
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return hoverCapable;
}

/**
 * Track the small-viewport breakpoint (mirrors the CSS `max-width: 767px`).
 * On mobile the dropdown is rendered through a portal to `document.body` with
 * fixed positioning so it can't be clipped by an ancestor's overflow/transform
 * (the floating bar itself is a horizontal scroll box, and page layouts may add
 * `overflow`/`transform` ancestors — any of which would otherwise clip an
 * in-flow absolutely-positioned dropdown).
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 767px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
}

export function FloatingNav({
  categories,
  activeSection,
  onNavigate,
  className,
  moduleAccent,
}: FloatingNavProps) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The mobile dropdown is portaled to <body>, so it lives outside containerRef
  // — keep its own ref so the outside-pointerdown handler doesn't treat a tap
  // inside the sheet as an outside click.
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const activeGroup = activeGroupForSection(categories, activeSection);
  const hoverCapable = useHoverCapable();
  const isMobile = useIsMobile();

  // Fixed coordinates for the mobile portal sheet, anchored just under the bar.
  const [sheetPos, setSheetPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!openGroup || !isMobile) {
      setSheetPos(null);
      return;
    }
    const compute = () => {
      const wrap = containerRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const margin = 12; // matches the 0.75rem side gutter
      setSheetPos({
        top: rect.bottom + 4,
        left: margin,
        width: Math.max(0, window.innerWidth - margin * 2),
      });
    };
    compute();
    // Recompute while open — the bar is sticky but its offset can shift with
    // safe-area / scroll on some devices, and rotation changes the width.
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [openGroup, isMobile]);

  // Track scroll position — when the user scrolls past a threshold the
  // wrapper's glass effect intensifies (data-scrolled="true" in CSS).
  useEffect(() => {
    const scrollParent = containerRef.current?.closest(".module-content-area") ?? window;
    const handleScroll = () => {
      const scrollY = scrollParent === window
        ? window.scrollY
        : (scrollParent as HTMLElement).scrollTop;
      setIsScrolled(scrollY > 8);
    };
    scrollParent.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // check initial position
    return () => scrollParent.removeEventListener("scroll", handleScroll);
  }, []);

  const accentStyles = useMemo(() => {
    if (!moduleAccent) return undefined;
    return {
      "--module-accent": moduleAccent,
      "--module-accent-tint": hexToTint(moduleAccent, 0.06),
      "--module-accent-tint-hover": hexToTint(moduleAccent, 0.10),
      "--module-accent-tint-strong": hexToTint(moduleAccent, 0.14),
    } as React.CSSProperties;
  }, [moduleAccent]);

  // Close dropdown on any outside pointerdown (covers mouse + touch + pen)
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      // The portaled mobile sheet is outside containerRef — don't close when
      // tapping inside it (that would steal the click from menu items).
      if (sheetRef.current?.contains(target)) return;
      setOpenGroup(null);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  // Close on Escape — accessibility expectation for menus
  useEffect(() => {
    if (!openGroup) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenGroup(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [openGroup]);

  const handleMouseEnter = useCallback(
    (group: string) => {
      if (!hoverCapable) return;
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setOpenGroup(group);
    },
    [hoverCapable],
  );

  const handleMouseLeave = useCallback(() => {
    if (!hoverCapable) return;
    closeTimerRef.current = setTimeout(() => setOpenGroup(null), 180);
  }, [hoverCapable]);

  const renderDropdownItems = (cat: FloatingNavCategory) =>
    cat.items.map((item) => {
      const Icon = item.icon;
      const isSectionActive = item.id === activeSection;
      const hasChange =
        (item.changeCount != null && item.changeCount > 0) || item.changeLabel;
      return (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className={cn(
            "floating-nav-dropdown-item",
            isSectionActive && "floating-nav-dropdown-item-active",
          )}
          onClick={() => {
            onNavigate(item.id);
            setOpenGroup(null);
          }}
        >
          <div
            className={cn(
              "floating-nav-dropdown-icon",
              isSectionActive && "floating-nav-dropdown-icon-active",
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="floating-nav-dropdown-text">
            <span className="floating-nav-dropdown-label">
              {item.label}
              {hasChange && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center rounded-full bg-blue-500 text-white text-[10px] font-semibold leading-none h-[1.125rem] min-w-[1.125rem] px-1"
                  role="status"
                  aria-label={item.changeLabel ?? `${item.changeCount} new`}
                >
                  {item.changeLabel ?? item.changeCount}
                </span>
              )}
            </span>
            <span className="floating-nav-dropdown-desc">{item.description}</span>
          </div>
        </button>
      );
    });

  return (
    <div ref={containerRef} className="floating-nav-wrapper" data-scrolled={isScrolled}>
      <nav
        className={cn(
          "floating-nav",
          moduleAccent && "floating-nav--accented",
          className,
        )}
        style={accentStyles}
        role="navigation"
        aria-label="Module sections"
      >
        <div className="floating-nav-inner">
          {categories.map((cat) => {
            const isActive = cat.group === activeGroup;
            const isOpen = cat.group === openGroup;
            const GroupIcon = cat.groupIcon;
            const opensMenu = cat.items.length > 1;
            const hasGroupChanges = cat.items.some(
              (item) =>
                (item.changeCount != null && item.changeCount > 0) || item.changeLabel,
            );

            return (
              <div
                key={cat.group}
                className="floating-nav-category"
                onMouseEnter={opensMenu ? () => handleMouseEnter(cat.group) : undefined}
                onMouseLeave={opensMenu ? handleMouseLeave : undefined}
              >
                <button
                  type="button"
                  className={cn(
                    "floating-nav-category-btn",
                    isActive && "floating-nav-category-btn-active",
                  )}
                  aria-label={
                    opensMenu
                      ? `Open ${cat.group} menu`
                      : `Go to ${cat.items[0]?.label ?? cat.group}`
                  }
                  onClick={() => {
                    if (!opensMenu) {
                      onNavigate(cat.items[0].id);
                      setOpenGroup(null);
                      return;
                    }
                    setOpenGroup((current) =>
                      current === cat.group ? null : cat.group,
                    );
                  }}
                  aria-expanded={opensMenu ? isOpen : undefined}
                  aria-haspopup={opensMenu ? "menu" : undefined}
                >
                  {GroupIcon && (
                    <GroupIcon className="floating-nav-category-icon" />
                  )}
                  <span className="floating-nav-category-label">{cat.group}</span>
                  {opensMenu && cat.menuHint ? (
                    <span className="floating-nav-menu-hint">{cat.menuHint}</span>
                  ) : null}
                  {hasGroupChanges && !isOpen && (
                    <span
                      className="relative inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500"
                      aria-hidden="true"
                    >
                      <span className="absolute inset-0 inline-flex h-full w-full rounded-full bg-blue-500 opacity-75 animate-ping" />
                    </span>
                  )}
                  {opensMenu ? (
                    <svg
                      className={cn(
                        "floating-nav-chevron",
                        isOpen && "floating-nav-chevron-open",
                      )}
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="2.5,3.5 5,6.5 7.5,3.5" />
                    </svg>
                  ) : null}
                </button>

                {/* Desktop/tablet popover (anchored to the category, CSS
                 * positioning). On mobile the dropdown is portaled to <body>
                 * instead (see below) so no ancestor overflow can clip it. */}
                {!isMobile && opensMenu && (
                  <div
                    className={cn(
                      "floating-nav-dropdown",
                      isOpen && "floating-nav-dropdown-visible",
                    )}
                    role="menu"
                    aria-hidden={!isOpen}
                  >
                    {renderDropdownItems(cat)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Desktop backdrop — invisible/non-interactive; the outside-pointerdown
       * handler does the closing. (Mobile uses the portal backdrop below.) */}
      {openGroup && !isMobile && (
        <button
          type="button"
          aria-label="Close menu"
          className="floating-nav-backdrop"
          onClick={() => setOpenGroup(null)}
        />
      )}

      {/* Mobile dropdown — portaled to <body> with fixed positioning so it
       * overlays page content regardless of any ancestor's overflow, transform,
       * or stacking context (the bar itself is a horizontal scroll box, and the
       * page content area is its own scroll container). */}
      {isMobile && openGroup && sheetPos &&
        (() => {
          const openCategory = categories.find((c) => c.group === openGroup);
          if (!openCategory || openCategory.items.length <= 1) return null;
          return createPortal(
            <>
              <button
                type="button"
                aria-label="Close menu"
                className="floating-nav-portal-backdrop"
                onClick={() => setOpenGroup(null)}
              />
              <div
                ref={sheetRef}
                role="menu"
                className="floating-nav-sheet"
                style={{ top: sheetPos.top, left: sheetPos.left, width: sheetPos.width }}
              >
                {renderDropdownItems(openCategory)}
              </div>
            </>,
            document.body,
          );
        })()}

      {/* Module accent bar below the nav — hidden when dropdown is open
       * so it doesn't bleed into the dropdown's drop-shadow. */}
      {moduleAccent && (
        <div
          className="floating-nav-accent-bar"
          style={{
            background: `linear-gradient(90deg, ${moduleAccent}00, ${moduleAccent}cc, ${moduleAccent}00)`,
            opacity: openGroup ? 0 : undefined,
            transition: "opacity 0.15s ease",
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
